// All help copy of the super admin block, in one place. Each entry feeds a HelpPopover:
// what it does, optional examples ({ label, value }) and one watch-out. Generic examples only.

export const HELP = {
  provider: {
    label: 'Provider',
    what: 'The LLM provider that writes the answers.',
    watchOut: "Changing it resets the model to that provider's first tested model.",
  },
  model: {
    label: 'Model',
    what: 'The model used from the selected provider.',
    watchOut: 'Only a model that passed its Test under AI Providers & Models can be picked.',
  },
  profileVersion: {
    label: 'Profile version',
    what: 'Pins the agent profile to one version.',
    examples: [
      { label: '2', value: '2' },
      { label: 'Blank (live pointer)', value: '' },
    ],
    watchOut: 'Blank follows the live pointer, so the store moves with every profile release.',
  },
  useRelevaTokens: {
    label: 'Use Releva tokens',
    what: "Bills usage against Releva tokens instead of the account's own LLM credits.",
  },
  domainFields: {
    label: 'Brand, colour and size',
    what: "Read from the domain's product sync settings; the agent uses them to describe and filter products.",
  },
  fieldMapping: {
    label: 'Field mapping',
    what: 'Product custom fields the agent reads (Context) and searches by (Searchable), and up to 2 shown on product cards (Card). The label is the word the agent uses for the field.',
    watchOut: 'Empty means the agent works as before. Card rows show only when Card fields is on in Buttons & cards.',
  },
  count: {
    label: 'Count',
    what: 'When the shopper names an exact value of this field, search adds how many catalog products carry it and shows only those products.',
    examples: [
      { label: 'brand', value: 'brand' },
      { label: 'category', value: 'category' },
      { label: 'country', value: 'country' },
    ],
    watchOut:
      'Only for clean fields: a free-text field splits into many values. Compare the count with your site before switching it on.',
  },
  boost: {
    label: 'Boost',
    what: 'Ranks products carrying one of these values of the field above equally relevant ones.',
    examples: [
      { label: 'bestseller', value: 'bestseller' },
      { label: 'new', value: 'new' },
      { label: 'own-brand', value: 'own-brand' },
    ],
    watchOut: 'It re-ranks, it does not filter. Values must match the feed exactly, case included.',
  },
  priceDisplayRule: {
    label: 'Hide price when',
    what: 'Products whose value for the field is one of these show without a price: the agent says the price is not listed and the cards carry none.',
    watchOut: 'Values match exactly, case included.',
  },
  productsNumber: {
    label: 'Products per search',
    what: 'How many products one product search returns to the agent.',
    examples: [
      { label: '3', value: 3 },
      { label: '5 (default)', value: 5 },
      { label: '10', value: 10 },
    ],
    watchOut: 'More products make answers slower and longer.',
  },
  multiLocaleProducts: {
    label: 'Multi-locale products',
    what: 'For catalogs that ship products in several locales: product and feed search use the page locale instead of the domain default.',
    watchOut: 'Replies and knowledge base search always follow the page locale, whatever this says.',
  },
  agentPlaybook: {
    label: 'Playbook',
    what: 'One instruction per line on how to guide the shopper. To offer choices as buttons, say so here.',
    examples: [
      { label: 'Ask for the city first', value: 'Ask for the city before searching.' },
      { label: 'Choices as buttons', value: 'When the shopper has to choose, offer up to three options as buttons.' },
      { label: 'Dates before availability', value: 'Ask for the date before checking availability.' },
    ],
    watchOut: 'Policies, FAQs and opening hours belong in the knowledge base.',
  },
  learning: {
    label: 'Learning',
    what: "Suggestions to improve this agent: fixes drafted from down-voted answers, and answers users edited or promoted in Conversations. Run now rewrites each client correction once in the store's language, or marks it as already covered; the client's own text stays on the item. Nothing changes until you approve an item; approved items go to the playbook or the knowledge base.",
    examples: [
      { label: 'Playbook: Ask which size the shopper wears before suggesting shoes.' },
      { label: 'Knowledge: Q: Can I return a sale item? A: Yes, within 30 days with the receipt.' },
    ],
    watchOut: 'A playbook item drafted before the playbook was edited must be edited onto the current playbook first.',
  },
  learningRunNow: {
    label: 'Run now',
    what: "Reads the down-voted answers of the last 14 days that no item covers yet and drafts up to 5 fixes. In the same AI call it rewrites up to 10 pending client corrections as a playbook rule or knowledge answer in the store's language, once each, or marks them as already covered.",
    examples: [
      { label: 'Client wrote: tell them shipping is free above 50 → Knowledge: Q: Is shipping free? A: Yes, on orders over 50.' },
      { label: 'Client wrote: dont list 10 products → Playbook: Suggest at most three products at a time.' },
    ],
    watchOut: 'At most once an hour. No new down-votes and no corrections to rewrite, no AI call. Approve applies the AI version.',
  },
  learningEditPromote: {
    label: 'Edit vs Promote',
    what: 'In Conversations, the pencil on an answer rewrites it (a knowledge answer or a playbook rule); the star keeps a good answer as it is, as a knowledge item. Both wait here for approval.',
    examples: [
      { label: 'Edit (wrong fact): Delivery takes 2–3 working days, not 5.' },
      { label: 'Edit (behaviour): Ask for the date before checking availability.' },
      { label: 'Promote: A clear answer about the return window, kept word for word.' },
    ],
  },
  history: {
    label: 'History',
    what: 'Every saved change to the playbook and the agent settings, newest first: who made it, from where (a manual save, an approved Learning item or a restore) and a line diff. Restoring writes the old version as a new one, so nothing is lost.',
    examples: [
      { label: 'Undo a playbook edit: Restore playbook on the version before it.' },
      { label: 'Roll back a model or field mapping change: Restore all agent settings.' },
    ],
    watchOut:
      'The newest 50 versions are kept. Knowledge base items and widget appearance are not part of it, and a restore leaves both alone.',
  },
  orderStatusEnabled: {
    label: 'Order status lookup',
    what: "Answers order and tracking questions from the store's orders. Off: they go to support.",
    watchOut: "Only works when the store's orders reach us through its integration or a connector.",
  },
  contextToday: {
    label: 'Today',
    what: "Gives the agent today's date in the store's time zone, so it can resolve “tomorrow” or “this weekend”.",
  },
  contextPage: {
    label: 'Page context',
    what: 'Tells the agent which page the shopper is on (product, category, search or cart), so “this one” means the product on the page.',
    watchOut: 'On single-page shops the page is the first one until the shopper reloads.',
  },
  location: {
    label: 'Shopper location',
    what: 'Ask: the agent may offer a button to share an approximate location when the shopper wants something nearby.',
    watchOut:
      'Approximate location (city or district) is used for this conversation only and never stored without consent.',
  },
  ipCity: {
    label: 'IP city',
    what: "When nothing is shared, the agent gets the city of the shopper's IP address as an approximate location.",
    watchOut: 'VPNs and foreign IPs give no city; the agent then asks.',
  },
  actions: {
    label: 'Actions',
    what: 'Buttons the agent can add to a reply, an initial message or a product card. The type names the button; the function says what it does.',
    examples: [
      {
        label: 'Share location',
        value: {
          type: 'location',
          do: 'location',
          labels: { en: { text: 'Share my location' } },
          onCards: false,
          inReplies: true,
        },
      },
      {
        label: 'Ask for a call back',
        value: {
          type: 'callBack',
          do: 'ask',
          labels: { en: { text: 'Call me back' } },
          askText: { en: { text: 'Please call me back.' } },
          onCards: false,
          inReplies: true,
        },
      },
      {
        label: 'Book with a link',
        value: {
          type: 'book',
          do: 'openLink',
          labels: { en: { text: 'Book a time' } },
          onCards: false,
          inReplies: true,
        },
      },
    ],
    watchOut: "Links open only on the domain host or a Safe URL; a language without a label uses the widget's own wording.",
  },
  safeUrls: {
    label: 'Safe URLs',
    what: "Hosts besides the domain's own that action links may open.",
    examples: [
      { label: 'booking.example', value: 'booking.example' },
      { label: '*.shop.example', value: '*.shop.example' },
    ],
    watchOut: '*.shop.example covers subdomains, not shop.example itself.',
  },
  productCard: {
    label: 'Card options',
    what: 'Adds to the product cards under a reply: the price on list cards, the Card fields from the field mapping, and buttons for actions shown on cards.',
    watchOut: "All off keeps today's cards. Merchant CSS on the cards may need a look once it is on.",
  },
  consentProviders: {
    label: 'Consent banner providers',
    what: 'Elements matching these selectors are stripped from every URL page imported into the knowledge base, before its text is extracted.',
    watchOut: 'Every listed provider is always applied.',
  },
  browserSteps: {
    label: 'Browser steps',
    what: "Run on every URL page this store imports, before a document's own steps. Remove steps strip elements; the others run in the headless browser first.",
    watchOut: "Saving doesn't re-import: use Re-fetch source on a document.",
  },
  documentsNumber: {
    label: 'Documents in Store section',
    what: 'How many knowledge base documents the Store section lists for the agent.',
    examples: [
      { label: '3', value: 3 },
      { label: '5 (default)', value: 5 },
      { label: '10', value: 10 },
    ],
    watchOut: 'It does not change how many knowledge base results a search retrieves.',
  },
  zIndexEnabled: {
    label: 'Custom z-index',
    what: 'Overrides the default z-index of every chat widget layer.',
  },
  zIndex: {
    label: 'Z-index base',
    what: 'The z-index the widget layers count up from.',
    examples: [
      { label: '99999990 (default)', value: 99999990 },
      { label: '10000', value: 10000 },
    ],
    watchOut: "Too low and the store's own popups cover the widget.",
  },
};

// onUse for a HelpPopover on a plain chat bot field: sets the example and returns its undo.
export const applyExample = (handleChange, name, previous) => (value) => {
  handleChange({ target: { name, value } });
  return () => handleChange({ target: { name, value: previous } });
};
